import { Fragment, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { api } from '@shared/api';
import { ModelLogo } from '@shared/brand';
import { idr, percent } from '@shared/format';
import { providerOf, type CatalogModel } from '../catalog';
import { Alert, Icon, InfoTip, Modal, Segmented } from './ui';
import '../styles/billing.css';

export interface Project {
  id: string;
  name: string;
  environment: string;
  daily_budget_idr: number | null;
  monthly_budget_idr: number | null;
  fallback_policy: string[];
  status: string;
  active_keys: number;
  month_spend_idr: number;
  created_at?: string;
}

interface WeekActivity {
  series: { spend_idr: Record<string, number[]> };
}

const ENVIRONMENTS = [
  { id: 'DEVELOPMENT', label: 'Development' },
  { id: 'STAGING', label: 'Staging' },
  { id: 'PRODUCTION', label: 'Production' },
] as const;

const MAX_FALLBACKS = 5;

export function loadWeekSpend(): Promise<Record<string, number[]> | null> {
  return api<WeekActivity>('/v1/usage/activity?range=7d&group_by=project')
    .then((body) => body.series.spend_idr)
    .catch(() => null);
}

function meterColor(ratio: number): string {
  if (ratio >= 0.9) return 'var(--status-critical)';
  if (ratio >= 0.7) return 'var(--status-warning)';
  return 'var(--accent)';
}

export function BudgetBlock({
  title,
  limitLabel,
  spentLabel,
  spent,
  limit,
  tip,
}: {
  title: string;
  limitLabel: string;
  spentLabel: string;
  spent: number | null;
  limit: number | null;
  tip: ReactNode;
}) {
  const ratio = limit && spent !== null ? spent / limit : 0;
  return (
    <div className="bl-budget">
      <div className="bl-budget-title">
        {title}
        <InfoTip label={`Tentang ${limitLabel.toLowerCase()}`}>{tip}</InfoTip>
      </div>
      <dl className="bl-rows">
        <div>
          <dt>{limitLabel}</dt>
          <dd className={limit ? 'strong' : 'muted'}>{limit ? idr(limit) : 'Tanpa batas'}</dd>
        </div>
        <div>
          <dt>{spentLabel}</dt>
          <dd className="strong">{spent === null ? '—' : idr(spent)}</dd>
        </div>
      </dl>
      {limit ? (
        <>
          <div
            className="meter"
            role="meter"
            aria-valuenow={Math.round(ratio * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${spentLabel} dari ${limitLabel.toLowerCase()}`}
          >
            <span style={{ width: `${Math.min(100, Math.max(ratio * 100, ratio > 0 ? 1 : 0))}%`, background: meterColor(ratio) }} />
          </div>
          <div className="bl-budget-sub">
            {percent(Math.min(ratio, 9.99) * 100)} dari batas · sisa {idr(Math.max(0, (limit ?? 0) - (spent ?? 0)))}
          </div>
        </>
      ) : (
        <div className="bl-budget-sub">Request tidak dibatasi biaya.</div>
      )}
    </div>
  );
}

export const DAILY_TIP = (
  <>
    Total biaya semua key di proyek ini per hari. Request yang membuat total melewati batas ditolak dengan error <code>budget_exceeded</code>.
  </>
);
export const MONTHLY_TIP = (
  <>
    Sama seperti batas harian, tetapi dihitung per bulan kalender. Cocok sebagai pengaman tagihan.
  </>
);

export function FallbackChain({ slugs, models }: { slugs: string[]; models: Map<string, CatalogModel> }) {
  return (
    <div className="bl-chain">
      {slugs.map((slug, i) => {
        const model = models.get(slug);
        return (
          <Fragment key={slug}>
            {i > 0 && <Icon name="right" size={14} className="bl-chain-sep" />}
            <span className="bl-chain-step" title={slug}>
              <span className="bl-chain-n">{i + 1}</span>
              <ModelLogo model={slug} family={model?.family} provider={model ? providerOf(model) : undefined} size={20} />
              {model?.display_name ?? slug}
            </span>
          </Fragment>
        );
      })}
    </div>
  );
}

export const FALLBACK_TIP = (
  <>
    Model yang diminta selalu dicoba dulu. Bila provider gagal (rate limit, timeout, atau error 5xx), model cadangan dicoba berurutan.
    Error karena saldo, anggaran, atau isi request tidak memicu cadangan.
  </>
);

export function ProjectModal({
  project,
  models,
  onClose,
  onSaved,
}: {
  project: Project | null;
  models: CatalogModel[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(project?.name ?? '');
  const [environment, setEnvironment] = useState(project?.environment ?? 'DEVELOPMENT');
  const [daily, setDaily] = useState(project?.daily_budget_idr?.toString() ?? '');
  const [monthly, setMonthly] = useState(project?.monthly_budget_idr?.toString() ?? '');
  const [fallbacks, setFallbacks] = useState<string[]>(project?.fallback_policy ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bySlug = useMemo(() => new Map(models.map((m) => [m.id, m])), [models]);
  // chat only
  const choices = useMemo(
    () =>
      models
        .filter((m) => (m.modality ?? 'text') === 'text' || fallbacks.includes(m.id))
        .sort((a, b) => providerOf(a).localeCompare(providerOf(b)) || a.id.localeCompare(b.id)),
    [models, fallbacks],
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name: name.trim(),
      environment,
      daily_budget_idr: daily === '' ? null : Number(daily),
      monthly_budget_idr: monthly === '' ? null : Number(monthly),
      fallback_policy: fallbacks,
    };
    try {
      if (project) await api(`/v1/projects/${project.id}`, { method: 'PATCH', body });
      else await api('/v1/projects', { method: 'POST', body });
      onSaved();
    } catch (err) {
      const status = (err as { status?: number }).status;
      setError(status === 403 ? 'Hanya Owner atau Admin organisasi yang bisa membuat dan mengubah proyek.' : (err as Error).message);
      setBusy(false);
    }
  };

  const toggleFallback = (slug: string) => {
    setFallbacks((current) =>
      current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug].slice(0, MAX_FALLBACKS),
    );
  };

  const move = (index: number, delta: number) => {
    setFallbacks((current) => {
      const next = [...current];
      const target = index + delta;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  return (
    <Modal title={project ? 'Ubah proyek' : 'Proyek baru'} onClose={onClose} width={620}>
      {error && <Alert kind="error">{error}</Alert>}
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="pname">Nama</label>
          <input id="pname" value={name} onChange={(e) => setName(e.target.value)} required autoFocus={!project} placeholder="Misalnya: Chatbot toko" />
        </div>

        <div className="field">
          <span className="label label-tip">
            Lingkungan
            <InfoTip label="Tentang lingkungan">
              Penanda untuk memisahkan pemakaian uji coba dan produksi. Key proyek Production berawalan <code>aia_live_</code>, yang
              lain <code>aia_test_</code>. Saldo dan modelnya sama.
            </InfoTip>
          </span>
          <div>
            <Segmented label="Lingkungan" options={ENVIRONMENTS} value={environment as (typeof ENVIRONMENTS)[number]['id']} onChange={setEnvironment} />
          </div>
        </div>

        <div className="bl-modal-section">
          <div className="bl-modal-section-title">Batas biaya</div>
          <div className="bl-grid-2">
            <div className="field" style={{ marginBottom: 0 }}>
              <span className="label label-tip">
                <label htmlFor="pdaily">Batas harian</label>
                <InfoTip label="Tentang batas harian">{DAILY_TIP}</InfoTip>
              </span>
              <div className="bl-money">
                <input id="pdaily" type="number" min={0} value={daily} onChange={(e) => setDaily(e.target.value)} placeholder="Tanpa batas" />
              </div>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <span className="label label-tip">
                <label htmlFor="pmonthly">Batas bulanan</label>
                <InfoTip label="Tentang batas bulanan">{MONTHLY_TIP}</InfoTip>
              </span>
              <div className="bl-money">
                <input id="pmonthly" type="number" min={0} value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="Tanpa batas" />
              </div>
            </div>
          </div>
          <p className="field-hint" style={{ margin: '8px 0 0' }}>
            Kosongkan bila tidak ingin dibatasi. Batas ini berlaku untuk total semua key di proyek.
          </p>
        </div>

        <div className="bl-modal-section">
          <div className="row between">
            <span className="bl-modal-section-title label-tip">
              Model cadangan
              <InfoTip label="Tentang model cadangan">{FALLBACK_TIP}</InfoTip>
            </span>
            <span className="field-hint tabular">
              {fallbacks.length} dari {MAX_FALLBACKS}
            </span>
          </div>
          <p className="field-hint" style={{ margin: '2px 0 10px' }}>
            Pilih model sesuai urutan yang Anda mau. Kosongkan jika model tidak boleh diganti.
          </p>

          {fallbacks.length > 0 && (
            <div className="bl-order">
              {fallbacks.map((slug, i) => {
                const model = bySlug.get(slug);
                return (
                  <div className="bl-order-row" key={slug}>
                    <span className="bl-order-n">{i + 1}</span>
                    <ModelLogo model={slug} family={model?.family} provider={model ? providerOf(model) : undefined} size={24} />
                    <span className="grow bl-stack">
                      <span className="truncate" style={{ fontWeight: 600 }}>{model?.display_name ?? slug}</span>
                      <span className="mono truncate">{slug}</span>
                    </span>
                    <button type="button" className="btn btn-sm btn-ghost icon-btn" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Naikkan">
                      <Icon name="chevron" size={15} style={{ transform: 'rotate(180deg)' }} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost icon-btn"
                      onClick={() => move(i, 1)}
                      disabled={i === fallbacks.length - 1}
                      aria-label="Turunkan"
                    >
                      <Icon name="chevron" size={15} />
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost icon-btn" onClick={() => toggleFallback(slug)} aria-label="Hapus">
                      <Icon name="close" size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {choices.length === 0 ? (
            <span className="muted small">Belum ada model teks di katalog.</span>
          ) : (
            <div className="bl-picker">
              {choices.map((model) => {
                const index = fallbacks.indexOf(model.id);
                const on = index >= 0;
                return (
                  <button
                    type="button"
                    key={model.id}
                    className={on ? 'bl-pick on' : 'bl-pick'}
                    onClick={() => toggleFallback(model.id)}
                    disabled={!on && fallbacks.length >= MAX_FALLBACKS}
                    title={model.id}
                    aria-pressed={on}
                  >
                    <ModelLogo model={model.id} family={model.family} provider={providerOf(model)} size={24} />
                    <span className="truncate">{model.display_name}</span>
                    {on && <span className="bl-pick-n">{index + 1}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="bl-modal-foot">
          <button type="button" className="btn" onClick={onClose}>
            Batal
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim()}>
            {busy && <span className="spinner" />}
            {project ? 'Simpan perubahan' : 'Buat proyek'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
